import React, { ReactNode } from 'react';
import Select, { components, OptionProps } from 'react-select';
import { ParsedShaderSet, ShaderId } from './parser/parser';
import { ShaderHelp } from './ShaderPropsEditor';
import { selectStyles } from './styles/selectStyles';

export interface SelectedShadersEditorProps {
  parsedShaders: ParsedShaderSet;
  activeShaders: ShaderId[];
  setActiveShaders: (shaders: ShaderId[]) => void;

  onPopupWindow: (contents: ReactNode) => void;
}

// An editor that allows the user to select shaders which shaders they want to be included in the compiled glsl.
// It presents the user with the list of dependencies of the active shaders and lets them select shaders to fufill those dependencies
export default function SelectedShadersEditor(props: SelectedShadersEditorProps) {
  const { parsedShaders, activeShaders, setActiveShaders, onPopupWindow } = props;

  // Get dependencies of current shaders + if/what shaders is providing an implementation of that dependency
  const depends = parsedShaders.getDepends(activeShaders, 0).map((depend) => {
    const implementors = parsedShaders.getProvides(depend.id);
    const selectedImplementors = implementors.filter((shader) => activeShaders.includes(shader))
    
    return {
      name: depend,
      implementors: implementors,
      curImplementor: selectedImplementors.length >= 1 ? selectedImplementors[0] : null
    } 
  });
  
  return (
    <div className="selectedShadersEdit">
      {depends.map((depend) => {
        // Generate list of available implementations for dropdown
        type OptionsType = {value: number | null, label: string, isDisabled?: boolean};

        let options: OptionsType[] = [{ value: null, label: "None", isDisabled: true }];
        options = options.concat(depend.implementors.map<OptionsType>((impl) => (
          { value: impl, label: parsedShaders.shaders[impl].name }
        )));

        // map of shader label => shader description
        let shaderDesc: {[label: string]: string} = {};
        depend.implementors.forEach((impl) => {
          const shader = parsedShaders.shaders[impl];
          if(shader.description !== "") {
            shaderDesc[shader.name] = shader.description;
          }
        });

        // custom Option component that displays shader help icon
        const Option = (props: OptionProps<OptionsType, false>) => (
          <div className="shaderSelectContainer">
            <components.Option {...props} />
            {props.label in shaderDesc &&
              <ShaderHelp description={shaderDesc[props.label]} onPopupWindow={onPopupWindow}></ShaderHelp>
            }
          </div>
        );

        // Get current implementor option
        const value = options.find((option) => option.value === depend.curImplementor);

        // When a dependency implementation is changed, deactivate the old implementor and add the new implementor to activeShaders
        const onChange = (option: OptionsType | null) => {
          if(option === null) return;
          // remove current implementor
          let newActiveShaders = activeShaders;
          newActiveShaders = activeShaders.filter((shader) => shader !== depend.curImplementor);
          // add new implementor
          if(option.value !== null) {
            newActiveShaders.push(option.value);
          }
          setActiveShaders(parsedShaders.removeNonImplementing(newActiveShaders, 0));
        };

        return (
          <div key={depend.name.id} className="shaderSelect">
            <div className="shaderDepName">{depend.name.name}:</div>
            <div>
              <Select
                options={options}
                components={{ Option }}
                value={value}
                onChange={onChange}
                styles={selectStyles}
                className="selectComponent"
              ></Select>
            </div>
          </div>
        );
      })}
    </div>
  );
}